/* eslint-disable react/jsx-boolean-value */
import React, { type PropsWithChildren, useContext, useLayoutEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { assert } from 'ts-essentials';

import NavigationConfirmationEnvironmentContext from '../NavigationConfirmationEnvironment/context';
import useNavigationConfirmationTrigger from '../useNavigationConfirmationTrigger';
import NavigationBlockerContext from './context';

const NavigationBlocker: React.FC<PropsWithChildren> = ({ children }) => {
  const parentContext = useContext(NavigationBlockerContext);
  const environmentContext = useContext(NavigationConfirmationEnvironmentContext);

  assert(!parentContext, 'only a single NavigationBlocker is allowed');
  assert(
    environmentContext !== null,
    'no NavigationConfirmationEnvironmentContext found in render tree',
  );

  const { tryNavigatingAway, requiresUserConfirmationBeforeNavigation } =
    useNavigationConfirmationTrigger(environmentContext.environmentId);

  const isRequestingUserInput = useRef(false);
  const blocker = useBlocker(({ historyAction }) => {
    if (isRequestingUserInput.current) {
      // we currently ask for user confirmation - block any other navigation as well
      return true;
    }
    // don't block navigation when we replace the current route which very likely indicates a
    // forced navigation triggered by us
    if (historyAction === 'REPLACE') {
      return false;
    }
    return requiresUserConfirmationBeforeNavigation();
  });

  useLayoutEffect(() => {
    if (isRequestingUserInput.current) return;

    if (blocker.state === 'blocked') {
      isRequestingUserInput.current = true;
      tryNavigatingAway(
        () => {
          isRequestingUserInput.current = false;
          blocker.proceed();
        },
        () => {
          isRequestingUserInput.current = false;
          blocker.reset();
        },
      );
    }
  }, [blocker, tryNavigatingAway]);

  return (
    <NavigationBlockerContext.Provider value={true}>{children}</NavigationBlockerContext.Provider>
  );
};

export default NavigationBlocker;
